<script>
import ServiceCard from '../../services/ServiceCard.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import AddServiceModal from '../AddServiceModal.vue';
import LinkServiceModal from '../LinkServiceModalV2.vue';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import ToggleModal from '../../services/ToggleModal.vue';

export default {
  name: 'ServiceTab',
  components: {
    ServiceCard,
    LoadingState,
    ErrorState,
    EmptyState,
    PaginationBar,
    AddServiceModal,
    LinkServiceModal,
    ToggleModal,
  },
  data(){
    return {
      isAddingService: false,
      showAddModal: false,
      showLinkModal: false,
      debouncedUpdateQuery: null,
      toggleItem: null,
      showToggleModal: false,
    }
  },
  created() {
    this.debouncedUpdateQuery = debounce((value) => {
      this.$store.dispatch('serviceV2/updateFiltersAndFetch', { q: value });
      this.$store.dispatch('branchServices/updateFiltersAndFetch', { q: value });
    }, 300);
  },
  computed: {
    isOnboardingLinkService() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'link_service_to_branch';
    },
    serviceOptions() {
      return this.$store.state.branchServices.branchServices;
    },
    branchId() {
      return this.$route.params.branchId;
    },
    // Status list has to be merged into service list to get all the info required
    // Endpoint needs to be updated
    serviceList() {
      return this.$store.state.serviceV2.services || [];
    },
    statusList() {
      return this.$store.state.branchServices.branchServices || [];
    },
    mappedServices() {
      if (!this.serviceList || !this.statusList || !this.branchId) {
        return [];
      }

      const result = this.serviceList.map(serviceItem => {

        const branchServiceMatch = this.statusList.find(branchItem => {
          const match =
            branchItem.service.id === serviceItem.service.id &&
            branchItem.branch.id === this.branchId;

          return match;
        });

        const mapped = {
          ...serviceItem,
          assignment: branchServiceMatch?.assignment || null,
          effective: branchServiceMatch?.effective || null
        };

        return mapped;
      });

      return result;
    },
    pagination() {
      return this.$store.getters['serviceV2/formattedPagination'];
    },
    isLoading() {
      return this.$store.state.serviceV2.isLoading || this.$store.state.branchServices.isLoading;
    },
    isError() {
      return this.$store.state.serviceV2.isError || this.$store.state.branchServices.isError;
    },
    filters() {
      return this.$store.state.serviceV2.filters;
    },
    filterActive: {
      get() {
        return this.filters.active;
      },
      set(value) {
        this.$store.dispatch('serviceV2/updateFiltersAndFetch', { active: value });
        this.$store.dispatch('branchServices/updateFiltersAndFetch', { active: value });
      },
    },
    filterQuery: {
      get() {
        return this.filters.q;
      },
      set(value) {
        this.debouncedUpdateQuery(value);
      }
    },
    isFilterActive() {
      return this.filterBranch || this.filterActive !== null || this.filterQuery;
    },
    emptyStateMessage() {
      if (this.isFilterActive) {
        return 'No services match your current filters.';
      }
      return 'Try assigning a service to this branch to get started.';
    },
  },
  methods: {
    async fetchServices() {
      try {
        await this.$store.dispatch('serviceV2/updateFiltersAndFetch', { branch_id: this.branchId });
      } catch (err) {
        useAlert(`Failed to load services: ${err}`);
        console.error(err);
      }
    },
    async fetchServiceStatus() {
      try {
        await this.$store.dispatch('branchServices/updateFiltersAndFetch', { branch_id: this.branchId });
      } catch (err) {
        useAlert(`Failed to load service status: ${err}`);
        console.error(err);
      }
    },
    handleOpenToggle(item) {
      this.toggleItem = item;
      this.$nextTick(() => {
        this.showToggleModal = true;
      });
    },
    handleCloseToggle(){
      this.showToggleModal = false;
      this.$nextTick(() => {
        this.toggleItem = null;
      });
    },
    fetchPreviousPage() {
      if (this.pagination.currentPage > 1) {
        let prev = this.pagination.currentPage - 1;
        this.$store.dispatch('serviceV2/updatePaginationAndFetch', { page: prev });
        this.$store.dispatch('branchServices/updatePaginationAndFetch', { page: prev });
      }
    },
    fetchNextPage() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        let next = this.pagination.currentPage + 1;
        this.$store.dispatch('serviceV2/updatePaginationAndFetch', { page: next });
        this.$store.dispatch('branchServices/updatePaginationAndFetch', { page: next });
      }
    },
    updatePageSize(newSize) {
      this.$store.dispatch('serviceV2/updatePaginationAndFetch', { page: 1, page_size: newSize });
      this.$store.dispatch('branchServices/updatePaginationAndFetch', { page: 1, page_size: newSize });
    },
    handleCloseLink() {
      this.showLinkModal = false;
    },
    handleOpenLink() {
      this.showLinkModal = true;
    },
    async clearFilters() {
      await this.$store.dispatch('serviceV2/updateFiltersAndFetch', { 
        branch_id: this.branchId, active: null, q: null 
      });
    },
    handleNavigate(id) {
      this.$router.push({
        name: 'organization.branches.details.service',
        params: { serviceId: id }
      });
    },
    async refreshPage() {
      await this.fetchServices();
      await this.fetchServiceStatus();
    }
  },
  async mounted() {
    await this.refreshPage();
  }
}
</script>

<template>
  <div class="px-8 pb-4 overflow-y-auto">
    <!-- Modals -->
    <AddServiceModal v-if="showAddModal" @close="showAddModal = false" @refresh="refreshPage" />
    <LinkServiceModal
      v-if="showLinkModal"
      :services="serviceList"
      @close="handleCloseLink"
      @refresh="refreshPage"
    />
    <ToggleModal
      v-if="showToggleModal"
      :service="toggleItem"
      :branchId="branchId"
      :is-global="false"
      @close="handleCloseToggle"
      @refresh="refreshPage"
    />

    <!-- Toolbar -->
    <div class="w-full flex justify-end gap-4 mb-4">
      <woot-button color-scheme="secondary" icon="service" @click="$router.push({ name: 'organization.services' })">
        Manage Services
      </woot-button>
      <div class="relative inline-block">
        <woot-button icon="add" @click="handleOpenLink">
          Add Service to Branch
        </woot-button>
        <!-- Onboarding Hint Tooltip -->
        <div v-if="isOnboardingLinkService" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
          <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
            Hint: Click to link service to this branch <fluent-icon icon="caret-up" />
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="w-full flex gap-4 items-end">
      <input 
        v-model="filterQuery" 
        placeholder="Enter your query here..."
        class="border px-2 py-1.5 border-slate-200 rounded-md flex-1 mb-4"
      />

      <div class="flex-1">
        <label class="text-sm">Active Status</label>
        <select v-model="filterActive">
          <option :value="null">All</option>
          <option :value="true">Active</option>
          <option :value="false">Inactive</option>
        </select>
      </div>

      <woot-button color-scheme="secondary" @click="clearFilters" :disabled="!isFilterActive" class="mb-4">Clear Filters</woot-button>
    </div>

    <!-- Pagination -->
    <PaginationBar
      v-if="!isLoading && !isError && mappedServices.length !== 0"
      :pagination="pagination"
      :loading="isLoading"
      @previous="fetchPreviousPage"
      @next="fetchNextPage"
      @page-size-change="updatePageSize"
    />

    <LoadingState v-if="isLoading" message="Loading services..." />

    <ErrorState v-else-if="!isLoading && isError" />

    <EmptyState
      v-else-if="!isLoading && !isError && mappedServices.length === 0"
      title="No Service Found"
      :message="emptyStateMessage"
    />

    <!-- Service Cards -->
    <div v-else-if="!isLoading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <ServiceCard
        v-for="(service, index) in mappedServices"
        :key="service.service.id"
        :service="service"
        :loading="false"
        :is-branch="true"
        :branch-id="branchId"
        :is-first="index === 0"
        @refresh="refreshPage"
        @toggle="handleOpenToggle"
        @navigate="handleNavigate"
      />
    </div>
  </div>
</template>

<style scoped>
@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0) translateX(4px);
  }
  50% {
    transform: translateY(-5px) translateX(4px);
  }
}

.animate-bounce-subtle {
  animation: bounce-subtle 2s ease-in-out infinite;
}
</style>